// Lets a developer point at the element they're reporting a bug on. Highlights
// whatever is under the cursor, and resolves with that element on click (or null
// on Esc / right-click cancel). Runs on the live page BEFORE the screen capture,
// so nothing it draws ends up in the screenshot.

const OVERLAY_ATTR = "data-bug-picker";

export const pickPageElement = () =>
  new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(null);
      return;
    }

    let currentEl = null;
    const previousCursor = document.body.style.cursor;

    const highlight = document.createElement("div");
    highlight.setAttribute(OVERLAY_ATTR, "highlight");
    Object.assign(highlight.style, {
      position: "fixed",
      zIndex: "2147483647",
      pointerEvents: "none",
      border: "2px solid #e11d48",
      background: "rgba(225, 29, 72, 0.12)",
      borderRadius: "2px",
      top: "0px",
      left: "0px",
      width: "0px",
      height: "0px",
      transition: "top 40ms, left 40ms, width 40ms, height 40ms",
    });

    const label = document.createElement("div");
    label.setAttribute(OVERLAY_ATTR, "label");
    Object.assign(label.style, {
      position: "fixed",
      zIndex: "2147483647",
      pointerEvents: "none",
      font: "600 12px/1.4 system-ui, sans-serif",
      color: "#fff",
      background: "#e11d48",
      padding: "2px 6px",
      borderRadius: "3px",
      whiteSpace: "nowrap",
      top: "0px",
      left: "0px",
      display: "none",
    });

    const banner = document.createElement("div");
    banner.setAttribute(OVERLAY_ATTR, "banner");
    banner.textContent =
      "Click the element with the issue  ·  Esc to skip";
    Object.assign(banner.style, {
      position: "fixed",
      zIndex: "2147483647",
      pointerEvents: "none",
      top: "12px",
      left: "50%",
      transform: "translateX(-50%)",
      font: "600 13px/1.4 system-ui, sans-serif",
      color: "#fff",
      background: "rgba(17, 24, 39, 0.92)",
      padding: "8px 14px",
      borderRadius: "6px",
      boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    });

    document.body.appendChild(highlight);
    document.body.appendChild(label);
    document.body.appendChild(banner);
    document.body.style.cursor = "crosshair";

    const isOwnNode = (node) => node && node.hasAttribute?.(OVERLAY_ATTR);

    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || isOwnNode(el)) return;

      currentEl = el;
      const r = el.getBoundingClientRect();
      highlight.style.top = `${r.top}px`;
      highlight.style.left = `${r.left}px`;
      highlight.style.width = `${r.width}px`;
      highlight.style.height = `${r.height}px`;

      const tag = el.tagName.toLowerCase();
      label.textContent = `${tag}${el.id ? `#${el.id}` : ""}  ${Math.round(
        r.width
      )}×${Math.round(r.height)}`;
      label.style.display = "block";
      // Sit the label just above the box, clamped into the viewport.
      const labelTop = r.top - 24 < 4 ? r.bottom + 4 : r.top - 24;
      label.style.top = `${Math.max(4, labelTop)}px`;
      label.style.left = `${Math.max(4, r.left)}px`;
    };

    const finish = (result) => {
      cleanup();
      resolve(result);
    };

    const onClick = (e) => {
      // Intercept before the real page reacts (navigation, submit, etc.).
      e.preventDefault();
      e.stopPropagation();
      finish(currentEl || document.elementFromPoint(e.clientX, e.clientY));
    };

    const onContextMenu = (e) => {
      e.preventDefault();
      finish(null);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish(null);
      }
    };

    function cleanup() {
      window.removeEventListener("mousemove", onMove, true);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("contextmenu", onContextMenu, true);
      window.removeEventListener("keydown", onKeyDown, true);
      highlight.remove();
      label.remove();
      banner.remove();
      document.body.style.cursor = previousCursor;
    }

    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("contextmenu", onContextMenu, true);
    window.addEventListener("keydown", onKeyDown, true);
  });

export default pickPageElement;
